import { useMemo } from "react";
import { useQuery } from "react-query";
import { AxiosError } from "axios";
import { authAxios as axios } from "shared-auth";
import {
  GET_SKILLS_FACETS,
  getFacetsCategories,
  SkillsFacetsType,
  IGetFacetsResponse,
} from "shared-services";
import { useErrorBoundary } from "component-library";
import { HALManager, UserManager } from "core";
import { mapFacetsToSkills } from "pages/Profile/helpers/map-facets-to-skills";
import { usePublicView } from "pages/Profile/hooks/usePublicView";
import { useGetPublicProfile } from "pages/Profile/hooks/useGetPublicProfile";
import { getApiUrl } from "services/UrlBuilder";
import Skill from "models/Skill";

export const GET_SKILLS_QUERY_KEY = "GET_SKILLS_QUERY_KEY";

export type IMappedSkills = [string, [string, boolean, string][]][];

export function useGetSkills(): {
  skills: IMappedSkills;
  isLoading: boolean;
} {
  const captureError = useErrorBoundary();
  const { isPublicView } = usePublicView();
  const { data: publicProfileData, isLoading: isPublicProfileLoading } =
    useGetPublicProfile();

  const { data: facets, isLoading: isFacetsLoading } = useQuery<
    IGetFacetsResponse,
    AxiosError
  >(
    GET_SKILLS_FACETS,
    async () => {
      const { data } = await getFacetsCategories(
        Object.values(SkillsFacetsType)
      );

      return data;
    },
    {
      onError: captureError,
    }
  );

  const { data: skillsData = [], isLoading: isSkillsLoading } = useQuery<
    Skill[],
    AxiosError
  >(
    GET_SKILLS_QUERY_KEY,
    async () => {
      const { data } = await axios.get(
        getApiUrl(HALManager.getSkillsUrl(UserManager.userId))
      );

      return data;
    },
    {
      enabled: !isPublicView,
      cacheTime: 0,
      onError: captureError,
    }
  );

  const skills = isPublicView ? publicProfileData?.skills || [] : skillsData;

  const mappedSkills = useMemo(() => {
    return facets && skills ? mapFacetsToSkills(facets, skills) : [];
  }, [facets, skills]);

  const isLoading =
    isPublicProfileLoading || isFacetsLoading || isSkillsLoading;

  return {
    skills: mappedSkills,
    isLoading,
  };
}
